import { db } from './firebase';
import firebase from 'firebase/compat/app';
import {
  normalizeTouchpoints,
  resolveConversationTemplate,
} from './conversationTemplateService.js';
import { getAllDefaultConversationTemplates } from '../constants/defaultConversationTemplates';

const DEFAULT_TOUCHPOINT = 'securelink';

function normalizeTouchpoint(touchpoint) {
  const value = typeof touchpoint === 'string' ? touchpoint.trim() : '';
  return value || DEFAULT_TOUCHPOINT;
}

function sanitizeTemplateRef(ref) {
  if (!ref || typeof ref !== 'object') return null;
  const source = typeof ref.source === 'string' ? ref.source.trim() : '';
  const templateId = typeof ref.templateId === 'string' ? ref.templateId.trim() : '';
  const adminId = typeof ref.adminId === 'string' ? ref.adminId.trim() : '';
  if (!source || !templateId) return null;
  const clean = { source, templateId };
  if (adminId) clean.adminId = adminId;
  return clean;
}

function getClinicTouchpointDoc(clinicId, touchpoint) {
  return db
    .collection('clinics')
    .doc(clinicId)
    .collection('touchpointConfig')
    .doc(normalizeTouchpoint(touchpoint));
}

function getAdminTouchpointDoc(adminId, touchpoint) {
  return db
    .collection('chartmindAdmins')
    .doc(adminId)
    .collection('touchpointDefaults')
    .doc(normalizeTouchpoint(touchpoint));
}

export async function getClinicTouchpointDefault(clinicId, touchpoint = DEFAULT_TOUCHPOINT) {
  if (!clinicId) return null;
  try {
    const doc = await getClinicTouchpointDoc(clinicId, touchpoint).get();
    if (!doc.exists) return null;
    const data = doc.data() || {};
    return sanitizeTemplateRef(data.templateRef);
  } catch (error) {
    console.warn('[touchpointRegistryService] getClinicTouchpointDefault failed', error);
    return null;
  }
}

export async function setClinicTouchpointDefault(clinicId, touchpoint, templateRef) {
  if (!clinicId) throw new Error('clinicId is required');
  const cleanRef = sanitizeTemplateRef(templateRef);
  await getClinicTouchpointDoc(clinicId, touchpoint).set(
    {
      touchpoint: normalizeTouchpoint(touchpoint),
      templateRef: cleanRef,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    },
    { merge: true }
  );
}

export async function getAdminTouchpointDefault(adminId, touchpoint = DEFAULT_TOUCHPOINT) {
  if (!adminId) return null;
  try {
    const doc = await getAdminTouchpointDoc(adminId, touchpoint).get();
    if (!doc.exists) return null;
    const data = doc.data() || {};
    return sanitizeTemplateRef(data.templateRef);
  } catch (error) {
    console.warn('[touchpointRegistryService] getAdminTouchpointDefault failed', error);
    return null;
  }
}

export async function setAdminTouchpointDefault(adminId, touchpoint, templateRef) {
  if (!adminId) throw new Error('adminId is required');
  const cleanRef = sanitizeTemplateRef(templateRef);
  await getAdminTouchpointDoc(adminId, touchpoint).set(
    {
      touchpoint: normalizeTouchpoint(touchpoint),
      templateRef: cleanRef,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    },
    { merge: true }
  );
}

async function findSystemTemplateRefForContext({ touchpoint, specialty }) {
  const normalizedTouchpoint = normalizeTouchpoint(touchpoint);
  if (normalizedTouchpoint === 'targeted') {
    // MEDICATIONS TOUCHPOINT TODO: In v1, medications review configuration lives on the PET
    // (checkInTemplates.medicationsReview field) rather than as a separate touchpoint. A future
    // 'medications-review' touchpoint would require PatientCheckInPage to call getTemplateForTouchpoint()
    // a second time and sequence a second conversation step. See PatientCheckInPage shouldShowMedsReview
    // for the current conditional.
    return { source: 'system', templateId: 'default-review-of-systems' };
  }
  const specialtyKey = typeof specialty === 'string' ? specialty.trim() : '';
  const systemRows = getAllDefaultConversationTemplates();
  const eligible = systemRows.filter(row =>
    normalizeTouchpoints(row).includes(normalizedTouchpoint)
  );
  if (!eligible.length) return null;

  const specialtyExact = specialtyKey
    ? eligible.find(row => row.specialty === specialtyKey)
    : null;
  if (specialtyExact) {
    return { source: 'system', templateId: specialtyExact.id };
  }

  const general = eligible.find(row => row.specialty === 'general');
  if (general) {
    return { source: 'system', templateId: general.id };
  }

  return { source: 'system', templateId: eligible[0].id };
}

/**
 * Resolve conversation template for a specific touchpoint using fallback order:
 * explicit ref -> clinic default -> admin default -> system defaults.
 *
 * @param {{
 *   touchpoint?: string,
 *   clinicId?: string,
 *   adminId?: string,
 *   specialty?: string,
 *   explicitRef?: { source: string, templateId: string, adminId?: string } | null
 * }} context
 * @returns {Promise<Object|null>}
 */
export async function getTemplateForTouchpoint(context = {}) {
  const normalizedTouchpoint = normalizeTouchpoint(context.touchpoint);

  const explicitRef = sanitizeTemplateRef(context.explicitRef);
  if (explicitRef) {
    const resolved = await resolveConversationTemplate(explicitRef);
    if (resolved) return resolved;
  }

  const clinicRef = await getClinicTouchpointDefault(context.clinicId, normalizedTouchpoint);
  if (clinicRef) {
    const resolved = await resolveConversationTemplate(clinicRef);
    if (resolved) return resolved;
  }

  const adminDefaultRef = await getAdminTouchpointDefault(context.adminId, normalizedTouchpoint);
  if (adminDefaultRef) {
    const resolved = await resolveConversationTemplate(adminDefaultRef);
    if (resolved) return resolved;
  }

  const systemRef = await findSystemTemplateRefForContext({
    touchpoint: normalizedTouchpoint,
    specialty: context.specialty,
  });
  if (!systemRef) return null;
  return resolveConversationTemplate(systemRef);
}

